import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Refresh,
  Search,
  History,
  People,
  LocalMovies,
  AttachMoney,
  Visibility,
  Star
} from '@mui/icons-material';
import { brandColors } from '../theme/theme';
import legacyQuorumService from '../lib/services/legacyQuorumService';
import { useFilmSubscription } from '../hooks/useFilmSubscription';
import { FILM_TIERS } from '../lib/filmSubscriptionTiers';
import BlurredContent from '../components/Subscription/BlurredContent';

const LegacyQuorumDashboard = () => {
  const navigate = useNavigate();
  const { hasFilmTier, forceUnlocked } = useFilmSubscription();
  const isProfessionalAccess = forceUnlocked || hasFilmTier(FILM_TIERS.PROFESSIONAL);
  const [films, setFilms] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState(''); // Input value (updates immediately)
  const [searchTerm, setSearchTerm] = useState(''); // Debounced search term (triggers API call)
  const [sortBy, setSortBy] = useState('film_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFilms, setTotalFilms] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const searchTimeoutRef = useRef(null);

  const filmsPerPage = 20; // Use API default for faster loading

  // Debounce search input - update searchTerm after user stops typing for 500ms
  useEffect(() => {
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout to update searchTerm
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1); // Reset to first page when searching
    }, 500); // 500ms debounce delay

    // Cleanup timeout on unmount or when searchInput changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Fetch data when searchTerm, page, sort, or sortOrder changes
  useEffect(() => {
    fetchData();
  }, [currentPage, sortBy, sortOrder, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build API parameters for server-side pagination, filtering, and sorting
      const apiParams = {
        page: currentPage,
        limit: filmsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      // Add search filter if provided
      if (searchTerm.trim()) {
        apiParams.film_name = searchTerm.trim();
      }

      // Fetch films with server-side pagination and filtering
      const filmsResponse = await legacyQuorumService.getFilms(apiParams);
      
      if (filmsResponse.success) {
        const transformedFilms = filmsResponse.data.films.map(film => 
          legacyQuorumService.transformFilmData(film)
        );
        setFilms(transformedFilms);
        setTotalPages(filmsResponse.data.pagination.totalPages);
        setTotalFilms(filmsResponse.data.pagination.total);
      } else {
        throw new Error('Failed to fetch films data');
      }

      // Calculate stats from the films data (only on first load or when not searching)
      if (currentPage === 1 && !searchTerm.trim()) {
        const statsResponse = await legacyQuorumService.getDashboardStats();
        if (statsResponse.success) {
          setStats(statsResponse.data);
        }
      }

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching legacy Quorum data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value); // Update input immediately (debounced searchTerm update happens in useEffect)
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setCurrentPage(1);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleFilmClick = (filmId) => {
    navigate(`/film-tracking/legacy-quorum/film/${filmId}`);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)}%`;
  };

  const getTrendIcon = (value) => {
    if (value === null || value === undefined) return <TrendingFlat />;
    if (value > 0) return <TrendingUp />;
    if (value < 0) return <TrendingDown />;
    return <TrendingFlat />;
  };

  const getTrendColor = (value) => {
    if (value === null || value === undefined) return 'text.secondary';
    if (value > 0) return 'success.main';
    if (value < 0) return 'error.main';
    return 'text.secondary';
  };

  if (loading && films.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ color: brandColors.primary, mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Loading Legacy Quorum Dashboard...
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  const requireProfessional = (content, featureName = 'Legacy Quorum Analytics', blurAmount = 12) => {
    if (isProfessionalAccess) {
      return content;
    }
    return (
      <BlurredContent requiredTier={FILM_TIERS.PROFESSIONAL} featureName={featureName} blurAmount={blurAmount}>
        {content}
      </BlurredContent>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <History sx={{ color: brandColors.secondary, mr: 2, fontSize: '2rem' }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: brandColors.primary }}>
              Legacy Quorum Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Historical film tracking data from legacy Quorum system
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRefresh} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!isProfessionalAccess && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Preview mode: data is blurred until you upgrade to the Film Professional plan.
          </Alert>
        )}

        {/* Legacy Data Notice */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Legacy Data Notice:</strong> This dashboard displays historical film tracking data from the legacy Quorum system. 
            Data may be incomplete or formatted differently from current tracking systems.
          </Typography>
        </Alert>
      </Box>

      {/* Performance Metrics Bar */}
      {requireProfessional(
        <Paper sx={{ p: 3, mb: 4, background: `linear-gradient(135deg, ${brandColors.secondary}08 0%, ${brandColors.secondary}15 100%)` }}>
          <Typography variant="h6" sx={{ mb: 3, color: brandColors.secondary, fontWeight: 600 }}>
            Legacy Data Overview
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: brandColors.primary, fontWeight: 700 }}>
                  {stats.totalFilms || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Films
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: brandColors.primary, fontWeight: 700 }}>
                  {stats.dateRange || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Date Range
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: brandColors.primary, fontWeight: 700 }}>
                  {stats.dataQuality || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Data Quality
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ color: brandColors.primary, fontWeight: 700 }}>
                  {stats.totalSurveys || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Surveys
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search films..."
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortBy} onChange={handleSortChange} label="Sort By">
                <MenuItem value="film_name">Film Name</MenuItem>
                <MenuItem value="release_year">Release Year</MenuItem>
                <MenuItem value="latest_awareness">Latest Awareness</MenuItem>
                <MenuItem value="latest_interest">Latest Interest</MenuItem>
                <MenuItem value="avg_awareness">Average Awareness</MenuItem>
                <MenuItem value="avg_interest">Average Interest</MenuItem>
                <MenuItem value="survey_count">Survey Count</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} onChange={handleSortOrderChange} label="Order">
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Films Grid */}
      {requireProfessional(
        <Grid container spacing={3}>
          {films.map((film) => (
            <Grid item xs={12} sm={6} md={4} key={film.id}>
            <Card 
              onClick={() => handleFilmClick(film.id)}
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                border: `2px solid ${brandColors.secondary}20`,
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: brandColors.secondary,
                  boxShadow: `0 8px 25px ${brandColors.secondary}20`,
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ flexGrow: 1, p: 3 }}>
                {/* Film Header */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                      {film.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {film.releaseYear || 'Unknown Year'}
                    </Typography>
                  </Box>
                  <Chip 
                    label="Legacy" 
                    size="small" 
                    sx={{ 
                      backgroundColor: brandColors.secondary + '20',
                      color: brandColors.secondary,
                      fontWeight: 600
                    }} 
                  />
                </Box>

                {/* Survey Info */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <People sx={{ fontSize: '1rem', mr: 0.5, verticalAlign: 'middle' }} />
                    {film.surveyCount} surveys
                  </Typography>
                </Box>

                {/* Metrics */}
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 1.5, 
                        backgroundColor: `${brandColors.awareness}08`,
                        border: `1px solid ${brandColors.awareness}30`,
                        borderRadius: 1.5 
                      }}>
                        <Visibility sx={{ fontSize: '1.2rem', color: brandColors.awareness, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Awareness
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.awareness }}>
                          {formatPercentage(film.avgAwareness)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 1.5, 
                        backgroundColor: `${brandColors.highInterest}08`,
                        border: `1px solid ${brandColors.highInterest}30`,
                        borderRadius: 1.5 
                      }}>
                        <Star sx={{ fontSize: '1.2rem', color: brandColors.highInterest, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Interest
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.highInterest }}>
                          {formatPercentage(film.avgInterest)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 1.5, 
                        backgroundColor: `${brandColors.theaterIntent}08`,
                        border: `1px solid ${brandColors.theaterIntent}30`,
                        borderRadius: 1.5 
                      }}>
                        <LocalMovies sx={{ fontSize: '1.2rem', color: brandColors.theaterIntent, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Theater
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.theaterIntent }}>
                          {formatPercentage(film.avgLocation)}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ 
                        textAlign: 'center', 
                        p: 1.5, 
                        backgroundColor: `${brandColors.willingToPay}08`,
                        border: `1px solid ${brandColors.willingToPay}30`,
                        borderRadius: 1.5 
                      }}>
                        <AttachMoney sx={{ fontSize: '1.2rem', color: brandColors.willingToPay, mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Willing to Pay
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: brandColors.willingToPay }}>
                          {formatPercentage(film.avgPayToSee)}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                {/* Volatility Indicators */}
                {(film.awarenessVolatility !== null || film.interestVolatility !== null) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Volatility
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {film.awarenessVolatility !== null && (
                        <Chip
                          icon={getTrendIcon(film.awarenessVolatility)}
                          label={`Aware: ${film.awarenessVolatility.toFixed(1)}%`}
                          size="small"
                          sx={{ 
                            color: getTrendColor(film.awarenessVolatility),
                            backgroundColor: getTrendColor(film.awarenessVolatility) + '10'
                          }}
                        />
                      )}
                      {film.interestVolatility !== null && (
                        <Chip
                          icon={getTrendIcon(film.interestVolatility)}
                          label={`Interest: ${film.interestVolatility.toFixed(1)}%`}
                          size="small"
                          sx={{ 
                            color: getTrendColor(film.interestVolatility),
                            backgroundColor: getTrendColor(film.interestVolatility) + '10'
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

              </CardContent>
            </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Loading Overlay */}
      {loading && (
        <Box sx={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(255,255,255,0.8)', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <CircularProgress size={60} sx={{ color: brandColors.primary }} />
        </Box>
      )}

      {/* Footer Info */}
      {lastUpdated && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Last updated: {lastUpdated.toLocaleString()} | Showing {films.length} of {totalFilms} films (Page {currentPage} of {totalPages})
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default LegacyQuorumDashboard;
